(function () {
  'use strict';

  if (window.__radianMarqueeScrollInitialized) return;
  window.__radianMarqueeScrollInitialized = true;

  // Scroll parallax marquee: the strip drifts on its own and scrolling the page
  // pushes it further along. The drift is a Web Animation on the track, so it
  // runs on the compositor and an idle page does no work for it; scrolling only
  // seeks that animation. While the page scrolls the drift waits, and it picks
  // up again AUTO_RESUME_DELAY after the last scroll.
  var SELECTOR = '.marquee-strip[data-scroll-parallax="true"]';
  var SCROLL_MULTIPLIER = 0.8;
  var AUTO_RESUME_DELAY = 140;
  var controllers = new WeakMap();
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function modulo(value, divisor) {
    if (!divisor) return 0;
    return ((value % divisor) + divisor) % divisor;
  }

  function MarqueeScrollController(root) {
    this.root = root;
    this.track = root.querySelector('.marquee-track');
    this.loopWidth = 0;
    this.animation = null;
    this.visible = false;
    this.scrolling = false;
    this.hovered = false;
    this.focused = false;
    this.scrollTimer = 0;
    this.lastScrollY = window.scrollY;
    this.direction = root.dataset.direction === 'left_to_right' ? 1 : -1;
    this.duration = Math.max(parseFloat(getComputedStyle(root).getPropertyValue('--marquee-speed')) || 20, 1) * 1000;
    this.pauseOnHover = root.classList.contains('marquee-strip--pause-on-hover');

    this.onScroll = this.onScroll.bind(this);
    this.onIntersection = this.onIntersection.bind(this);
    this.measure = this.measure.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onFocusChange = this.onFocusChange.bind(this);
    this.onMotionChange = this.onMotionChange.bind(this);

    if (!this.track || typeof this.track.animate !== 'function') return;

    this.measure();
    this.root.classList.add('is-scroll-parallax-ready');
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.root.addEventListener('pointerenter', this.onPointerEnter);
    this.root.addEventListener('pointerleave', this.onPointerLeave);
    this.root.addEventListener('focusin', this.onFocusChange);
    this.root.addEventListener('focusout', this.onFocusChange);
    reducedMotion.addEventListener('change', this.onMotionChange);

    this.resizeObserver = new ResizeObserver(this.measure);
    this.resizeObserver.observe(this.track);

    this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
      rootMargin: '15% 0px'
    });
    this.intersectionObserver.observe(this.root);
  }

  // One loop of the track (its content is doubled). The animation runs the
  // track by one loop per --marquee-speed, in the strip's direction.
  MarqueeScrollController.prototype.measure = function () {
    if (!this.track) return;

    var nextWidth = this.track.scrollWidth / 2;
    if (!nextWidth || nextWidth === this.loopWidth) return;

    var progress = this.animation ? modulo(Number(this.animation.currentTime) || 0, this.duration) / this.duration : 0;
    if (this.animation) this.animation.cancel();
    this.loopWidth = nextWidth;

    if (reducedMotion.matches) {
      this.animation = null;
      return;
    }

    var keyframes = this.direction < 0
      ? [{ transform: 'translate3d(0, 0, 0)' }, { transform: 'translate3d(' + (-nextWidth) + 'px, 0, 0)' }]
      : [{ transform: 'translate3d(' + (-nextWidth) + 'px, 0, 0)' }, { transform: 'translate3d(0, 0, 0)' }];
    this.animation = this.track.animate(keyframes, {
      duration: this.duration,
      iterations: Infinity,
      easing: 'linear'
    });
    this.animation.currentTime = progress * this.duration;
    this.sync();
  };

  MarqueeScrollController.prototype.sync = function () {
    if (!this.animation) return;
    var play = this.visible && !this.scrolling && !this.hovered && !this.focused && !reducedMotion.matches;
    if (play && this.animation.playState !== 'running') this.animation.play();
    else if (!play && this.animation.playState === 'running') this.animation.pause();
  };

  MarqueeScrollController.prototype.onScroll = function () {
    // Off-screen strips skip the scroll read entirely; onIntersection re-syncs lastScrollY on entry.
    if (!this.visible || !this.animation || !this.loopWidth) return;

    var scrollY = window.scrollY;
    var delta = scrollY - this.lastScrollY;
    this.lastScrollY = scrollY;

    if (!delta) return;

    // The scroll moves the strip along its direction, as the drift does.
    this.scrolling = true;
    this.sync();
    var time = (Number(this.animation.currentTime) || 0) + delta * SCROLL_MULTIPLIER / this.loopWidth * this.duration;
    this.animation.currentTime = modulo(time, this.duration);

    var self = this;
    clearTimeout(this.scrollTimer);
    this.scrollTimer = setTimeout(function () {
      self.scrolling = false;
      self.sync();
    }, AUTO_RESUME_DELAY);
  };

  MarqueeScrollController.prototype.onIntersection = function (entries) {
    this.visible = entries[0].isIntersecting;
    this.lastScrollY = window.scrollY;
    this.sync();
  };

  MarqueeScrollController.prototype.onPointerEnter = function () {
    if (!this.pauseOnHover) return;
    this.hovered = true;
    this.sync();
  };

  MarqueeScrollController.prototype.onPointerLeave = function () {
    this.hovered = false;
    this.sync();
  };

  MarqueeScrollController.prototype.onFocusChange = function () {
    var self = this;
    // focusout fires before the next element takes focus.
    setTimeout(function () {
      self.focused = self.root.contains(document.activeElement);
      self.sync();
    }, 0);
  };

  MarqueeScrollController.prototype.onMotionChange = function () {
    this.loopWidth = 0;
    if (this.animation) this.animation.cancel();
    this.animation = null;
    this.measure();
  };

  MarqueeScrollController.prototype.destroy = function () {
    clearTimeout(this.scrollTimer);
    if (this.animation) this.animation.cancel();
    this.animation = null;
    window.removeEventListener('scroll', this.onScroll);
    this.root.removeEventListener('pointerenter', this.onPointerEnter);
    this.root.removeEventListener('pointerleave', this.onPointerLeave);
    this.root.removeEventListener('focusin', this.onFocusChange);
    this.root.removeEventListener('focusout', this.onFocusChange);
    reducedMotion.removeEventListener('change', this.onMotionChange);
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    this.root.classList.remove('is-scroll-parallax-ready');
    controllers.delete(this.root);
  };

  function init(scope) {
    var roots = [];
    if (scope.matches && scope.matches(SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots = roots.concat(Array.from(scope.querySelectorAll(SELECTOR)));

    roots.forEach(function (root) {
      if (controllers.has(root)) return;
      controllers.set(root, new MarqueeScrollController(root));
    });
  }

  function destroy(scope) {
    var roots = [];
    if (scope.matches && scope.matches(SELECTOR)) roots.push(scope);
    if (scope.querySelectorAll) roots = roots.concat(Array.from(scope.querySelectorAll(SELECTOR)));

    roots.forEach(function (root) {
      var controller = controllers.get(root);
      if (controller) controller.destroy();
    });
  }

  init(document);
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event.target);
  });
})();
